import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useDb } from "../../contexts/DbContext"
import {
  type Place,
  type UserDetail,
  emptyPlace,
  accomodationTypes,
} from "../../types/global"
import { useAuth } from "../../contexts/AuthContext"
import { ChangeEvent } from "react"
import Modal from "../../primitives/modal"
import {
  Grid,
  TextField,
  InputAdornment,
  FormHelperText,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Select,
} from "@material-ui/core"
import { Autocomplete } from "@material-ui/lab"
import Button from "../../components/Button"
import TrailAssignment from "./components/TrailAssignment"
import * as S from "./styles"
import AddressInput from "./components/AddressInput"
import Images from "./components/Images"

export type Errors = {
  name: string
  type: string
  ico: string
  capacity: string
  trails: any[]
  trailsDistances: any[]
  distances: any[]
  phoneNumber: string
  email: string
  checkin: string
  checkout: string
  details: string
  detailsPrivate: string
}

const getWebParts = (web: string | undefined) => {
  if (!web) return { protocol: "https://", domain: "" }
  if (web.startsWith("http://")) {
    return { protocol: "http://", domain: web.substring(7) }
  }
  if (web.startsWith("https://")) {
    return { protocol: "https://", domain: web.substring(8) }
  }
  return { protocol: "https://", domain: web }
}

export default function HostProfiles() {
  const [error, setError] = useState("")
  const [validationErrors, setValidationErrors] = useState<Errors | null>(null)
  const [success, setSuccess] = useState("")
  const [loading, setLoading] = useState(false)
  const [places, setPlaces] = useState<Place[]>([])
  const [currentPlace, setCurrentPlace] = useState<Place>(emptyPlace)
  const [webProtocol, setWebProtocol] = useState("https://")
  const [refresh, setRefresh] = useState(false)
  const [trailsAssigned, setTrailsAssigned] = useState(1)
  const [facilities, setFacilities] = useState<string[]>([])
  const [imagesNew, setImagesNew] = useState<{ file: File; base64: string }[]>(
    [],
  )
  const [imagesToRemove, setImagesToRemove] = useState<
    { placeId: string; src: string }[]
  >([])
  const [currentUserDetail, setCurrentUserDetail] = useState<UserDetail | null>(
    null,
  )
  const navigate = useNavigate()
  const {
    getUserPlaces,
    updatePlace,
    getFacilities,
    addPlaceImage,
    removePlaceImage,
    uploadProgress,
    getUser,
  } = useDb()

  const { currentUser } = useAuth()

  useEffect(() => {
    getUserPlaces(currentUser.uid).then((querySnapshot: any) => {
      if (querySnapshot.empty) return // Place created for the first time
      setPlaces(
        querySnapshot.docs.map((doc: any) => {
          return { ...(doc.data() as Place), id: doc.id as string }
        }),
      )
      const loadedPlace = {
        ...querySnapshot.docs[0].data(),
        id: querySnapshot.docs[0].id,
      }
      setCurrentPlace(loadedPlace)
      setWebProtocol(getWebParts(loadedPlace.web).protocol)
    })
    getUser(currentUser.uid).then((querySnapshot: any) => {
      setCurrentUserDetail(querySnapshot.data())
    })
  }, [currentUser.uid, refresh, getUser, getUserPlaces])

  // Get all available facilities
  useEffect(() => {
    getFacilities()
      .then((querySnapshot: any) => {
        querySnapshot.forEach((doc: any) => {
          setFacilities((facilities) => [...facilities, doc.data().cs])
        })
      })
      .catch(() => {
        setError("Error getting facilities")
      })
    return () => {
      setFacilities([])
    }
  }, [getFacilities])

  useEffect(() => {
    setValidationErrors(null)
  }, [currentPlace])

  const handlePlaceChange = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const { name, value } = event.target
    if (!currentPlace || value === undefined) return

    setCurrentPlace((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  const webDomain = getWebParts(currentPlace.web).domain

  const handleWebDomainChange = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const domainValue = event.target.value
    const cleanDomain = domainValue.replace(/^https?:\/\//i, "")
    setCurrentPlace((prevState) => ({
      ...prevState,
      web: cleanDomain ? webProtocol + cleanDomain : "",
    }))
  }

  const handleWebProtocolChange = (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    const newProtocol = event.target.value as string
    setWebProtocol(newProtocol)
    setCurrentPlace((prevState) => {
      const { domain } = getWebParts(prevState.web)
      return {
        ...prevState,
        web: domain ? newProtocol + domain : "",
      }
    })
  }

  const handleHiddenChange = (value: boolean) => {
    setCurrentPlace((prevState) => ({
      ...prevState,
      hidden: value,
    }))
  }

  const handlePriceChange = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    if (event.target.value === undefined) return

    let priceTemp = event.target.value === "free" ? -1 : 0

    setCurrentPlace((prevState) => ({
      ...prevState,
      price: priceTemp,
    }))
  }

  const handleFacilitiesChange = (values: string[]) => {
    setCurrentPlace((prevState) => ({
      ...prevState,
      facilities: values,
    }))
  }

  async function handleSubmit(e?: React.MouseEvent<Element, MouseEvent>) {
    e?.preventDefault()

    let errors: any = {}

    if (currentPlace.address.length === 0) {
      setError("Adresa nemůže zůstat prázdná")
      return
    }

    if (currentPlace.name === "" || currentPlace.name === undefined)
      errors.name = "Jméno nemůže zůstat prázdné"
    if (currentPlace.type === undefined) errors.type = "Zvol typ ubytování"
    if (currentPlace.type === "commercial" && currentPlace.ico === undefined)
      errors.ico = "Pro komerční ubytování je třeba vyplnit IČO"
    if (isNaN(currentPlace.capacity)) errors.capacity = "Zvol počet osob"
    if (currentPlace.capacity % 1 !== 0) errors.capacity = "Zvol celé číslo"
    if (currentPlace.capacity <= 0)
      errors.capacity = "Vyplň alespoň číslo 1, nebo vyšší"

    if (currentPlace.trails === undefined) currentPlace.trails = []
    if (currentPlace.trailsDistances === undefined)
      currentPlace.trailsDistances = []
    if (currentPlace.distances === undefined) currentPlace.distances = []

    for (let id = 0; id < trailsAssigned; id++) {
      if (currentPlace.trails[id] === undefined) {
        if (errors.trails === undefined) errors.trails = []
        errors.trails[id] = "Vyber trasu"
      }
      if (
        isNaN(currentPlace.trailsDistances[id]) ||
        currentPlace.trailsDistances[id] < 0
      ) {
        if (errors.trailsDistances === undefined) errors.trailsDistances = []
        errors.trailsDistances[id] = "Vyber vzdálenost od začátku trasy"
      }
      if (isNaN(currentPlace.distances[id]) || currentPlace.distances[id] < 0) {
        if (errors.distances === undefined) errors.distances = []
        errors.distances[id] = "Vyber vzdálenost od trasy"
      }
    }

    if (Object.keys(errors).length !== 0) {
      setError("Formulář obsahuje chyby")
      window.scrollTo(0, 0)
      setValidationErrors(errors)
      return
    }

    setLoading(true)
    setError("")

    if (typeof currentPlace.hidden === "undefined") currentPlace.hidden = false

    const placeData = {
      id: currentPlace.id,
      name: currentPlace.name,
      type: currentPlace.type,
      capacity: currentPlace.capacity,
      trails: currentPlace.trails,
      trailsDistances: currentPlace.trailsDistances,
      distances: currentPlace.distances,
      price: currentPlace.price || 0,
      phoneNumber: currentPlace.phoneNumber || "",
      email: currentPlace.email || "",
      address: currentPlace.address,
      facilities: currentPlace.facilities,
      details: currentPlace.details || "",
      detailsPrivate: currentPlace.detailsPrivate || "",
      web: currentPlace.web || "",
      hidden: currentPlace.hidden,
      checkin: currentPlace.checkin || "",
      checkout: currentPlace.checkout || "",
    }

    try {
      let pid = await updatePlace(currentUser.uid, placeData)

      if (currentPlace.id) pid = currentPlace.id

      for (const image of imagesToRemove) {
        await removePlaceImage(image.placeId, image.src)
      }
      setImagesToRemove([])

      for (const image of imagesNew) {
        await addPlaceImage(image.file, pid)
      }
      setImagesNew([])
    } catch (error) {
      setError("Nepodařilo se uložit data: " + error)
    } finally {
      setRefresh(true)
      setLoading(false)
      setSuccess("Uloženo")
      setTimeout(() => {
        navigate("/")
      }, 2000)
    }
  }

  if (!currentPlace) return <>Loading ...</>

  return (
    <>
      <Modal
        $visible={success !== ""}
        autoHide={5}
        toggle={() => setSuccess("")}
        type="success"
      >
        {success +
          (uploadProgress < 100 && uploadProgress > 0
            ? " " + uploadProgress + " %"
            : "")}
      </Modal>
      <Modal $visible={error !== ""} autoHide={5} toggle={() => setError("")}>
        {error}
      </Modal>
      <S.Main>
        <S.Title>Moje ubytovaní</S.Title>
        {currentUserDetail?.role === "admin" && (
          <S.PlaceSelectContainer>
            {places.map((place: any) => (
              <Button
                key={place.id}
                onClick={() => setCurrentPlace(place)}
                blank
                color="white"
              >
                {place.name || place.id}
              </Button>
            ))}
            <Button onClick={() => setCurrentPlace(emptyPlace)}>+</Button>
          </S.PlaceSelectContainer>
        )}
        <S.TextFieldWrapper>
          <TextField
            label="Název ubytování"
            required
            variant="outlined"
            value={currentPlace.name || ""}
            onChange={handlePlaceChange}
            name="name"
            style={{ width: 300 }}
            error={Boolean(validationErrors?.name)}
            helperText={validationErrors?.name || ""}
          />
        </S.TextFieldWrapper>
        <S.TextFieldWrapper>
          <TextField
            label="Typ ubytování"
            select
            required
            variant="outlined"
            value={currentPlace.type || ""}
            onChange={handlePlaceChange}
            name="type"
            style={{ width: 300 }}
            error={Boolean(validationErrors?.type)}
            helperText={validationErrors?.type || ""}
          >
            {accomodationTypes.map((type) => (
              <MenuItem key={type.textId} value={type.textId}>
                {type.title}
              </MenuItem>
            ))}
          </TextField>
        </S.TextFieldWrapper>
        {currentPlace.type === "commercial" && (
          <S.TextFieldWrapper>
            <TextField
              required
              name="ico"
              label="IČO"
              type="text"
              value={currentPlace.ico || ""}
              error={Boolean(validationErrors?.ico)}
              helperText={validationErrors?.ico || ""}
              onChange={handlePlaceChange}
              variant="outlined"
            />
          </S.TextFieldWrapper>
        )}
        <S.TextFieldWrapper>
          <TextField
            style={{ width: 180 }}
            label="Kapacita"
            type="number"
            required
            value={currentPlace.capacity || ""}
            onChange={handlePlaceChange}
            name="capacity"
            variant="outlined"
            error={Boolean(validationErrors?.capacity)}
            helperText={validationErrors?.capacity || ""}
            InputProps={{
              inputProps: {
                min: 1,
              },
              endAdornment: (
                <InputAdornment position="end">Osob</InputAdornment>
              ),
            }}
          />
        </S.TextFieldWrapper>
        <S.TextFieldWrapper>
          <TextField
            style={{ width: 300 }}
            label="Telefonní číslo"
            type="tel"
            value={currentPlace.phoneNumber ?? currentUser.phoneNumber ?? ""}
            onChange={handlePlaceChange}
            name="phoneNumber"
            variant="outlined"
            error={validationErrors?.phoneNumber !== undefined}
          />
        </S.TextFieldWrapper>
        <S.TextFieldWrapper>
          <TextField
            style={{ width: 300 }}
            label="Email"
            type="email"
            value={currentPlace.email ?? currentUser.email ?? ""}
            onChange={handlePlaceChange}
            name="email"
            variant="outlined"
            error={validationErrors?.email !== undefined}
          />
        </S.TextFieldWrapper>
        <TrailAssignment
          currentPlace={currentPlace}
          setCurrentPlace={setCurrentPlace}
          trailsAssigned={trailsAssigned}
          setTrailsAssigned={setTrailsAssigned}
          setError={setError}
          validationErrors={validationErrors}
        />
        {trailsAssigned < 6 && (
          <S.Margin>
            <Button
              content="Přidat další trasu"
              onClick={() => setTrailsAssigned(trailsAssigned + 1)}
              color="gray"
              size="small"
            />
          </S.Margin>
        )}
        <S.TextFieldWrapper>
          <TextField
            label="Cena"
            select
            onChange={handlePriceChange}
            value={currentPlace.price === -1 ? "free" : "fixed"}
            required
            variant="outlined"
          >
            <MenuItem value="free">Dobrovolný příspěvek</MenuItem>
            <MenuItem value="fixed">Pevná cena</MenuItem>
          </TextField>
        </S.TextFieldWrapper>
        {currentPlace.price !== -1 && (
          <S.TextFieldWrapper>
            <TextField
              type="number"
              label="Cena v Kč"
              name="price"
              disabled={currentPlace.price === -1 ? true : false}
              required={currentPlace.price === -1 ? false : true}
              value={
                currentPlace?.price && currentPlace.price >= 0
                  ? currentPlace.price
                  : ""
              }
              onChange={handlePlaceChange}
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">Kč/os</InputAdornment>
                ),
              }}
            />
          </S.TextFieldWrapper>
        )}
        <S.TextFieldWrapper>
          <AddressInput
            currentPlace={currentPlace}
            setCurrentPlace={setCurrentPlace}
          />
        </S.TextFieldWrapper>
        <S.TextFieldWrapper>
          <Autocomplete
            style={{ maxWidth: "585px", minWidth: "300px" }}
            multiple
            freeSolo
            onChange={(_, values) => handleFacilitiesChange(values)}
            options={facilities}
            placeholder="Vyber ze seznamu nebo přidej vlastní ..."
            value={currentPlace.facilities}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Vybavení"
                variant="outlined"
                placeholder="Vyber ze seznamu nebo přidej vlastní ..."
              />
            )}
          />
        </S.TextFieldWrapper>
        <S.TextFieldWrapper>
          <TextField
            name="checkin"
            label="Příchod"
            type="text"
            value={currentPlace.checkin || ""}
            onChange={handlePlaceChange}
            variant="outlined"
          />
          <FormHelperText id="details-helper-text">
            Např. odpoledne, nebo 11 hod.
          </FormHelperText>
        </S.TextFieldWrapper>
        <S.TextFieldWrapper>
          <TextField
            name="checkout"
            label="Odchod"
            type="text"
            value={currentPlace.checkout || ""}
            onChange={handlePlaceChange}
            variant="outlined"
          />
          <FormHelperText id="details-helper-text">
            Např. dopoledne, nebo 11 hod.
          </FormHelperText>
        </S.TextFieldWrapper>
        <S.TextFieldWrapper>
          <TextField
            style={{ maxWidth: "585px", minWidth: "300px" }}
            name="details"
            label="Doplňující informace"
            multiline
            minRows={2}
            value={currentPlace.details || ""}
            onChange={handlePlaceChange}
            variant="outlined"
          />
          <FormHelperText id="details-helper-text">
            Uvidí všichni
          </FormHelperText>
        </S.TextFieldWrapper>
        <S.TextFieldWrapper>
          <TextField
            style={{ maxWidth: "585px", minWidth: "300px" }}
            name="detailsPrivate"
            label="Doplňující kontaktní informace"
            multiline
            minRows={2}
            value={currentPlace.detailsPrivate || ""}
            onChange={handlePlaceChange}
            variant="outlined"
          />
          <FormHelperText id="details-private-helper-text">
            Viditelné pouze registrovaným
          </FormHelperText>
        </S.TextFieldWrapper>
        <S.TextFieldWrapper>
          <TextField
            label="Web"
            type="text"
            value={webDomain}
            onChange={handleWebDomainChange}
            name="web"
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Select
                    value={webProtocol}
                    onChange={handleWebProtocolChange}
                    disableUnderline
                    style={{ fontSize: "inherit", marginRight: "4px" }}
                  >
                    <MenuItem value="https://">https://</MenuItem>
                    <MenuItem value="http://">http://</MenuItem>
                  </Select>
                </InputAdornment>
              ),
            }}
          />
        </S.TextFieldWrapper>
        <S.TextFieldWrapper>
          <FormControlLabel
            control={
              <Checkbox
                checked={currentPlace.hidden || false}
                name="hidden"
                onChange={(_, value) => handleHiddenChange(value)}
              />
            }
            label="Skrýt ubytování"
          />
        </S.TextFieldWrapper>
        <S.TextFieldWrapper>
          <Images
            currentPlace={currentPlace}
            setCurrentPlace={setCurrentPlace}
            imagesNew={imagesNew}
            setImagesNew={setImagesNew}
            setImagesToRemove={setImagesToRemove}
            uploadProgress={uploadProgress}
            imagesToRemove={imagesToRemove}
            loading={loading}
            setLoading={setLoading}
          />
        </S.TextFieldWrapper>
        <Grid item>
          <Button onClick={() => navigate(-1)} disabled={loading} color="gray">
            Zpět
          </Button>
          <Button disabled={loading} color="primary" onClick={handleSubmit}>
            Uložit
          </Button>
        </Grid>
      </S.Main>
    </>
  )
}
